import styled from "@emotion/styled";

import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { SvgIcon } from "@/components/helpers/ui/SvgIcon";
import { Box, Flex } from "@/components/layout/flexbox";
import { breakpoints } from "@/components/layout/Media";
import { Container } from "@/components/pieces/bootstrap";
import { BG_PURPLE, LIGHT_PURPLE } from "@/css/constants";

const ITEMS = [
  { key: "team-building", label: "Team Building" },
  { key: "virtual-leadership", label: "Virtual Leadership" },
  { key: "feedback", label: "Providing Feedback" },
  { key: "grow-manager", label: "Growing as a Manager" },
  { key: "exec-presence", label: "Exec Presence" },
  { key: "imposter-syndrome", label: "Imposter Syndrome" },
  { key: "manage-up", label: "Managing Up" },
  { key: "conflict", label: "Conflict Management" },
  { key: "room-to-grow", label: "Get room to grow" },
  { key: "change-roles", label: "Change roles" },
  { key: "find-career", label: "Find a career you love" },
  { key: "quit-boss", label: "Quit your boss" },
  { key: "whats-next", label: "Figure out what's next" },
  { key: "interviews", label: "Pass more interviews" },
  { key: "go-remote", label: "Go 100% remote" },
  { key: "new-industry", label: "Break into a new industry" },
  { key: "more-money", label: "Make more money" },
  { key: "resume", label: "Upgrade your resume" },
  { key: "use-education", label: "Use your new degree" },
  { key: "find-mission", label: "Do work with a mission" },
  { key: "find-direction", label: "Find Direction" },
  { key: "work-life", label: "Work-Life Balance" },
  { key: "anger", label: "Anger Management" },
  { key: "parenting", label: "Parenting" },
  { key: "emotion", label: "Emotional Well-Being" },
];

interface UseCasesProps {
  audience?: string;
  useCases?: string[];
  defaultItems?: typeof ITEMS;
  header?: React.ReactNode;
  numberOfItems?: number;
  isWhiteIcon?: boolean;
  iconSize?: number;
  labelStyle?: React.CSSProperties;
  backgroundColor?: string;
  className?: string;
  extraStyle?: React.CSSProperties;
  maxWidth?: number;
}

export function UseCases({
  audience,
  useCases = [],
  defaultItems = ITEMS,
  header,
  numberOfItems = 6,
  isWhiteIcon = false,
  iconSize = 105,
  labelStyle = {},
  backgroundColor = BG_PURPLE,
  className,
  extraStyle,
  maxWidth,
}: UseCasesProps) {
  const items = useCases
    ? defaultItems.filter((i) => useCases.includes(i.key))
    : defaultItems;
  const numberItems = numberOfItems
    ? numberOfItems
    : items.length % 6 === 0
      ? 6
      : items.length % 8 === 0
        ? 8
        : items.length;
  const StyledGrid = styled(Grid)`
    grid-template-columns: repeat(${numberItems}, 1fr);
  `;

  return (
    <Box
      pt={80}
      pb={110}
      style={{
        backgroundColor: backgroundColor,
        color: LIGHT_PURPLE,
        textAlign: "center",
        ...extraStyle,
      }}
    >
      <Container>
        <Box maxWidth={maxWidth} mx="auto">
          {header ? (
            header
          ) : (
            <>
              <h2 style={{ fontSize: 36, color: "white" }}>
                {audience === "D2C"
                  ? "Targeted sessions to help you break through"
                  : "Targeted sessions to help them break through"}
              </h2>
              <Box mt={3} fontSize={20}>
                {audience === "D2C"
                  ? `Your coach is equipped to help you navigate whatever life throws at
            you`
                  : "Your team gets access to incredible coaches to overcome common work challenges"}
              </Box>
            </>
          )}
          {items?.length > 0 && (
            <StyledGrid mt={80} className={className}>
              {items.map((item, index) => {
                return (
                  <Icon
                    key={index}
                    label={item.label}
                    icon={SvgIcon({ name: item.key, isWhite: isWhiteIcon })}
                    size={iconSize}
                    labelStyle={labelStyle}
                  />
                );
              })}
            </StyledGrid>
          )}
        </Box>
      </Container>
    </Box>
  );
}

interface IconProps {
  label: string;
  icon: string;
  size: number;
  labelStyle: ExplicitAny;
}

function Icon({ label, icon, size, labelStyle }: IconProps) {
  return (
    <Flex className="usecase-item" flexDirection="column" alignItems="center">
      <EmbedSvg width={size} src={icon} />
      <Flex className="usecase-text" flexDirection="column">
        <Box mt={3} fontSize={18} css={labelStyle}>
          {label}
        </Box>
      </Flex>
    </Flex>
  );
}

const Grid = styled(Box)`
  display: grid;
  grid-gap: 40px;

  svg {
    max-width: 100%;
  }

  @media screen and (max-width: ${breakpoints.lg}px) {
    grid-gap: 30px;
    grid-template-columns: repeat(6, 1fr);
  }

  @media screen and (max-width: ${breakpoints.md}px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media screen and (max-width: ${breakpoints.sm}px) {
    grid-template-columns: repeat(2, 1fr);

    &.mobile-single-line {
      grid-template-columns: repeat(1, 1fr);
      margin-top: 40px;

      .usecase-item {
        flex-direction: row;
      }

      .usecase-text {
        text-align: left;
        margin-left: 20px;
      }
    }
  }
`;
